import { ComponentRef } from '@angular/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { ElWindowComponent } from './window.component';
import { ElWindowConfig, ElWindowState, ElWindowStateChange } from './window.options';

/**
 * The `ElWindowRef` helps to manipulate window after it was created.
 * The window can be dismissed by using `close` method of the windowRef.
 * You can access rendered component as `componentRef` property of the windowRef.
 */
export class ElWindowRef {
  componentRef: ComponentRef<ElWindowComponent>;

  protected prevStateValue: ElWindowState;
  protected stateValue: ElWindowState;
  /**
   * Current window state.
   */
  get state() {
    return this.stateValue;
  }
  set state(newState: ElWindowState) {
    if (newState && this.stateValue !== newState) {
      this.prevStateValue = this.state;
      this.stateValue = newState;
      this.stateChange$.next({ oldState: this.prevStateValue, newState });
    }
  }

  protected stateChange$ = new ReplaySubject<ElWindowStateChange>(1);
  /**
   * Emits when window state change.
   */
  get stateChange(): Observable<ElWindowStateChange> {
    return this.stateChange$.asObservable();
  }

  protected _closed = false;
  protected closed$ = new Subject();
  /**
   * Emits when window was closed.
   */
  get onClose() {
    return this.closed$.asObservable();
  }

  constructor(public config: ElWindowConfig) {
    this.state = config.initialState;
  }

  /**
   * Minimize window.
   */
  minimize() {
    this.state = ElWindowState.MINIMIZED;
  }

  /**
   * Maximize window.
   */
  maximize() {
    this.state = ElWindowState.MAXIMIZED;
  }

  /**
   * Set window on top.
   */
  fullScreen() {
    this.state = ElWindowState.FULL_SCREEN;
  }

  toPreviousState() {
    this.state = this.prevStateValue;
  }

  /**
   * Closes window.
   * */
  close() {
    if (this._closed) {
      return;
    }

    this._closed = true;
    this.componentRef.destroy();
    this.stateChange$.complete();
    this.closed$.next();
    this.closed$.complete();
  }
}
